import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, Users, Trophy, Zap, Target, Search, Brain, 
  GraduationCap, CheckCircle, ArrowRight, Play, ChevronRight,
  Sparkles, Globe, BarChart3, Clock, Award, Crown, Briefcase,
  Code, FileText, UserCheck, TrendingUp, Shield, Star, Code2,
  ChevronDown, ArrowUpRight, Cpu, Database, Lock, LineChart,
  Activity, PieChart, Layers, GitBranch, Terminal, Gauge, LayoutDashboard
} from 'lucide-react';
import Logo from '../components/landing/Logo';
import GrandFooter from '../components/landing/GrandFooter';

const FadeInSection = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const CompanyLogo = () => <Logo subtitle="for Companies" size="md" />

const AnimatedLineChart = ({ data, color = '#171717', height = 60 }) => {
  const [animated, setAnimated] = useState(false);
  const chartRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
      { threshold: 0.3 }
    );
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  const max = Math.max(...data);
  const points = data.map((val, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - (val / max) * 100
  }));
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div ref={chartRef} style={{ height }} className="w-full relative">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id={`gradC-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.1" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${pathD} L 100 100 L 0 100 Z`} fill={`url(#gradC-${color.replace('#', '')})`} className={`transition-all duration-1000 ${animated ? 'opacity-100' : 'opacity-0'}`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" className={`transition-all duration-1000 ${animated ? 'opacity-100' : 'opacity-0'}`}
          style={{ strokeDasharray: 300, strokeDashoffset: animated ? 0 : 300, transition: 'stroke-dashoffset 1.5s cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
        {animated && points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="2.5" fill="#FFF" stroke={color} strokeWidth="2" className="animate-pulse" style={{ animationDelay: `${i * 0.1}s` }} />
        ))}
      </svg>
    </div>
  );
};

const AnimatedDonutChart = ({ segments, size = 80 }) => {
  const [animated, setAnimated] = useState(false);
  const chartRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setAnimated(true); }, { threshold: 0.3 });
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const radius = 35;
  const circumference = 2 * Math.PI * radius;
  let currentOffset = 0;

  return (
    <div ref={chartRef} style={{ width: size, height: size }} className="relative">
      <svg viewBox="0 0 100 100" className="transform -rotate-90">
        {segments.map((segment, idx) => {
          const segmentLength = (segment.value / total) * circumference;
          const offset = currentOffset;
          currentOffset += segmentLength;
          return (
            <circle key={idx} cx="50" cy="50" r={radius} fill="none" stroke={segment.color} strokeWidth="12"
              strokeDasharray={`${segmentLength} ${circumference}`}
              strokeDashoffset={animated ? -offset : circumference}
              className="transition-all duration-1000 ease-out"
              style={{ transitionDelay: `${idx * 150}ms` }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-black text-[#0A0A0A] tracking-tight">{total}%</span>
      </div>
    </div>
  );
};

const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) { setCount(end); clearInterval(timer); } 
      else { setCount(Math.floor(current)); }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [end]);
  return <>{count.toLocaleString()}{suffix}</>;
};

const CompanyLandingPage = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({ national_challenge_price: 9999 });
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  
  useEffect(() => {
    const token = localStorage.getItem('company_token');
    if (token) navigate('/company/dashboard');
    
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/company-pricing`);
        const data = await res.json();
        if (data.pricing) setPricing(data.pricing);
      } catch (err) { console.error('Failed to fetch pricing'); }
    };
    fetchPricing();

    const handleScroll = () => setIsNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-white font-sans text-[#0A0A0A] selection:bg-[#FEF2F2] selection:text-[#B91C1C]" data-testid="company-landing">
      
      {/* Sticky Glass-blur Nav */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${isNavScrolled ? 'bg-white/85 backdrop-blur-md border-b border-[#E5E5E5] shadow-sm py-3' : 'bg-transparent py-5'}`} data-testid="company-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/">
              <CompanyLogo />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#solutions" className="text-sm font-bold text-[#525252] hover:text-[#0A0A0A] transition-colors uppercase tracking-wide">Solutions</a>
              <a href="#pricing" className="text-sm font-bold text-[#525252] hover:text-[#0A0A0A] transition-colors uppercase tracking-wide">Pricing</a>
              <a href="#customers" className="text-sm font-bold text-[#525252] hover:text-[#0A0A0A] transition-colors uppercase tracking-wide">Customers</a>
            </nav>
            
            <div className="flex items-center gap-4">
              <Link to="/company/auth" className="hidden md:block text-sm font-bold text-[#0A0A0A] hover:text-[#B91C1C] transition-colors" data-testid="company-nav-signin">Sign In</Link>
              <Link to="/company/auth?mode=signup" className="px-6 py-2.5 bg-[#0A0A0A] hover:bg-[#171717] text-white rounded-full text-sm font-bold shadow-[0_4px_14px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.2)] hover:-translate-y-0.5 transition-all" data-testid="company-nav-cta">Book a Demo</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-28 md:pt-40 md:pb-36 min-h-[calc(100vh-3.5rem)] flex items-center overflow-hidden" data-testid="company-hero">
        <div className="absolute top-1/4 -right-1/4 text-[18vw] font-black text-[#0A0A0A] opacity-[0.025] -rotate-12 pointer-events-none select-none tracking-tighter leading-none">
          HIRE
        </div>
        
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative z-10 w-full">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <FadeInSection className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 mb-8">
                <span className="text-[11px] font-black uppercase tracking-widest text-[#B91C1C]">FOR HIRING TEAMS</span>
              </div>
              
              <h1 className="text-6xl md:text-7xl lg:text-[7rem] font-extrabold text-[#0A0A0A] tracking-tighter leading-[0.95] mb-10" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Hire developers <br />who already <br /><span className="relative inline-block">proved themselves.<motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }} className="absolute -bottom-1 left-0 h-3 bg-[#B91C1C]/25 -z-10"/></span>
              </h1>
              
              <p className="text-xl md:text-2xl text-[#525252] mb-12 leading-relaxed max-w-xl font-medium">
                Tap into a pre-vetted national talent pool. Filter by verified scores, run AI-proctored assessments, and close the best candidates faster.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/company/auth" className="px-9 py-5 bg-[#B91C1C] text-white rounded-full font-bold shadow-[0_8px_24px_rgba(185,28,28,0.25)] hover:shadow-[0_12px_32px_rgba(185,28,28,0.35)] hover:-translate-y-1 transition-all flex items-center gap-2 group" data-testid="company-hero-cta-primary">
                  Start Hiring <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="px-9 py-5 bg-white text-[#0A0A0A] border-[1.5px] border-[#E5E5E5] hover:border-[#0A0A0A] rounded-full font-bold transition-all flex items-center gap-2 group" data-testid="company-hero-cta-secondary">
                  <Play size={18} className="fill-current text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors" /> See Live Demo
                </button>
              </div>
            </FadeInSection>

            {/* Premium Pipeline Mockup */}
            <FadeInSection delay={0.2} className="lg:col-span-5 relative">
              <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#F5F5F5]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
                      <Target className="text-white" size={18} />
                    </div>
                    <div>
                      <div className="text-[#0A0A0A] font-bold text-sm">Hiring Pipeline</div>
                      <div className="text-[#A3A3A3] text-xs font-bold uppercase tracking-widest">SDE-1 Roles</div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {[
                    { label: 'Applied', value: 1240, width: '100%', color: 'bg-[#F5F5F5]', text: 'text-[#525252]' },
                    { label: 'Tested', value: 856, width: '80%', color: 'bg-[#FAFAF9]', border: 'border border-[#E5E5E5]', text: 'text-[#171717]' },
                    { label: 'Shortlisted', value: 124, width: '40%', color: 'bg-[#FEF2F2]', border: 'border border-[#B91C1C]/20', text: 'text-[#B91C1C]' },
                    { label: 'Hired', value: 12, width: '15%', color: 'bg-[#B91C1C]', text: 'text-white' }
                  ].map((stage, idx) => (
                    <div key={idx} className="relative h-12 flex items-center">
                      <div className={`absolute top-0 left-0 h-full rounded-r-xl transition-all duration-1000 ${stage.color} ${stage.border || ''}`} style={{ width: stage.width, zIndex: 0 }} />
                      <div className={`relative z-10 flex items-center justify-between w-full px-4 ${stage.text}`}>
                        <span className="font-bold text-sm tracking-wide">{stage.label}</span>
                        <span className="font-black text-lg">{stage.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Trust Strip Marquee */}
      <section className="py-10 bg-[#FAFAF9] border-y border-[#F5F5F5] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-6 text-center">
          <p className="text-[10px] font-black text-[#A3A3A3] uppercase tracking-widest">Trusted by 500+ elite engineering teams</p>
        </div>
        <div className="flex gap-16 items-center whitespace-nowrap animate-[scroll_30s_linear_infinite] opacity-60 hover:opacity-100 transition-opacity saturate-0 grayscale">
          {/* Duplicate set for smooth loop */}
          {[1, 2].map(set => (
            <React.Fragment key={set}>
              <div className="text-2xl font-black text-[#171717] tracking-tighter">TECHHIRE</div>
              <div className="text-2xl font-black text-[#171717] tracking-tighter">NEXUS<span className="font-light">LABS</span></div>
              <div className="text-2xl font-black text-[#171717] tracking-tighter uppercase font-mono">Atomic_AI</div>
              <div className="text-2xl font-black text-[#171717] tracking-tighter italic">Skyforge</div>
              <div className="text-2xl font-black text-[#171717] tracking-tighter">NORTHGATE</div>
              <div className="text-2xl font-black text-[#171717] tracking-tighter">HELIOS</div>
              <div className="text-2xl font-black text-[#171717] tracking-tighter font-serif">Velvet Cap.</div>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Trust Elements Row */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 border-t border-[#F5F5F5] pt-12">
            {[
              { icon: Shield, title: 'Verified Talent Pool', desc: 'Every developer goes through a 2-stage proctored AI verification before joining the pool.' },
              { icon: BarChart3, title: 'AI-Driven Insights', desc: 'Real-time analytics on candidate performance, coding patterns, and logical approaches.' },
              { icon: CheckCircle, title: '100% Compliance', desc: 'GDPR-ready, ISO-aligned recruitment process handling data securely.' }
            ].map((elem, idx) => (
              <div key={idx} className="flex flex-col gap-4">
                <elem.icon size={24} className="text-[#0A0A0A]" />
                <h4 className="text-lg font-black text-[#0A0A0A]">{elem.title}</h4>
                <p className="text-sm text-[#525252] leading-relaxed font-medium">{elem.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three-Pillar Solutions Grid */}
      <section id="solutions" className="py-24 bg-[#FAFAF9] border-t border-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-[#0A0A0A] tracking-tight mb-4">Complete Hiring Suite</h2>
            <p className="text-lg text-[#525252] font-medium max-w-2xl">One platform to run your entire technical recruitment pipeline smoothly.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Pillar 1 */}
            <div className="bg-white p-8 rounded-3xl border border-[#E5E5E5] hover:border-[#0A0A0A] hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-all group flex flex-col" data-testid="pillar-post-jobs">
              <div className="w-12 h-12 bg-[#FAFAF9] border border-[#E5E5E5] rounded-xl flex items-center justify-center mb-6">
                <Briefcase size={20} className="text-[#0A0A0A]" />
              </div>
              <h3 className="text-2xl font-black text-[#0A0A0A] mb-3">Post Jobs</h3>
              <p className="text-[#525252] mb-8 font-medium leading-relaxed">Reach 50K+ pre-vetted developers in minutes. Setup automated skill filters instantly.</p>
              <div className="mt-auto pt-6 border-t border-[#F5F5F5]">
                <Link to="/company/auth" className="text-[#B91C1C] font-bold flex items-center gap-1.5 text-sm group-hover:text-[#7F1D1D]">
                  Post a job <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Pillar 2 */}
            <div className="bg-[#0A0A0A] p-8 rounded-3xl border border-[#0A0A0A] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.2)] transition-all group flex flex-col" data-testid="pillar-view-candidates">
              <div className="w-12 h-12 bg-[#171717] rounded-xl flex items-center justify-center mb-6">
                <Users size={20} className="text-white" />
              </div>
              <h3 className="text-2xl font-black text-white mb-3">View Candidates</h3>
              <p className="text-[#A3A3A3] mb-8 font-medium leading-relaxed">AI-ranked, pre-tested talent with verified skill profiles and code playbacks.</p>
              <div className="mt-auto pt-6 border-t border-[#171717]">
                <Link to="/company/auth" className="text-white font-bold flex items-center gap-1.5 text-sm">
                  Browse talent <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Pillar 3 */}
            <div className="bg-white p-8 rounded-3xl border border-[#E5E5E5] hover:border-[#0A0A0A] hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-all group flex flex-col" data-testid="pillar-manage-listings">
              <div className="w-12 h-12 bg-[#FAFAF9] border border-[#E5E5E5] rounded-xl flex items-center justify-center mb-6">
                <LayoutDashboard size={20} className="text-[#0A0A0A]" />
              </div>
              <h3 className="text-2xl font-black text-[#0A0A0A] mb-3">Manage Listings</h3>
              <p className="text-[#525252] mb-8 font-medium leading-relaxed">One dashboard to run your entire pipeline, track metrics, and collaborate with your team.</p>
              <div className="mt-auto pt-6 border-t border-[#F5F5F5]">
                <Link to="/company/auth" className="text-[#0A0A0A] font-bold flex items-center gap-1.5 text-sm group-hover:text-[#B91C1C]">
                  See dashboard <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Insights Mockup */}
      <section className="py-24 bg-white border-t border-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-[#0A0A0A] tracking-tight mb-4">Deep Analytics Power</h2>
          <p className="text-[#525252] font-medium">Real-time candidate metrics, funnel drop-offs, and skill breakdowns.</p>
        </div>
        
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="bg-[#FAFAF9] rounded-3xl border border-[#E5E5E5] p-2 md:p-4">
            <div className="bg-white rounded-2xl border border-[#F5F5F5] shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6 md:p-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="col-span-2">
                  <div className="text-sm font-bold text-[#A3A3A3] uppercase tracking-widest mb-4">Weekly Assessment Volume</div>
                  <AnimatedLineChart data={[40, 60, 45, 80, 95, 75, 110]} color="#B91C1C" height={150} />
                </div>
                <div className="flex flex-col justify-center">
                  <div className="text-sm font-bold text-[#A3A3A3] uppercase tracking-widest mb-4">Candidate Scores</div>
                  <AnimatedDonutChart segments={[{ value: 45, color: '#0A0A0A' }, { value: 30, color: '#B91C1C' }, { value: 25, color: '#E5E5E5' }]} size={120} />
                  <div className="mt-4 flex flex-col gap-2 text-xs font-bold text-[#525252]">
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#0A0A0A]"></span> {">"}80% Score (45%)</div>
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#B91C1C]"></span> 60-80% Score (30%)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Candidate Stats - Animated Counters */}
      <section className="py-16 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
             <div className="text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">What you'll see in your dashboard</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x-0 md:divide-x divide-[#171717]">
            <div>
              <div className="text-4xl font-black mb-2 tracking-tighter"><AnimatedCounter end={4.2} />d</div>
              <div className="text-xs font-bold text-[#A3A3A3] uppercase tracking-widest">Avg. Time to Hire</div>
            </div>
            <div>
              <div className="text-4xl font-black mb-2 tracking-tighter"><AnimatedCounter end={84} />%</div>
              <div className="text-xs font-bold text-[#A3A3A3] uppercase tracking-widest">Pre-tested Applicants</div>
            </div>
            <div>
              <div className="text-4xl font-black mb-2 tracking-tighter text-[#B91C1C]"><AnimatedCounter end={100} />%</div>
              <div className="text-xs font-bold text-[#A3A3A3] uppercase tracking-widest">Identity Verified</div>
            </div>
            <div>
              <div className="text-4xl font-black mb-2 tracking-tighter"><AnimatedCounter end={65} />%</div>
              <div className="text-xs font-bold text-[#A3A3A3] uppercase tracking-widest">Offer Acceptance</div>
            </div>
          </div>
        </div>
      </section>

      {/* National Hiring Challenges Banner */}
      <section className="py-24 bg-white border-b border-[#F5F5F5]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="bg-[#FEF2F2] rounded-3xl border border-[#B91C1C]/20 p-8 md:p-12 relative overflow-hidden" data-testid="pricing-national-challenge">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#B91C1C] mix-blend-multiply opacity-[0.03] rounded-full blur-3xl"></div>
            
            <div className="grid md:grid-cols-5 gap-8 items-center relative z-10">
              <div className="col-span-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#B91C1C] mb-2 flex items-center gap-2"><Crown size={14} /> Premium Tier</div>
                <h2 className="text-3xl md:text-4xl font-black text-[#0A0A0A] mb-4 tracking-tight">National Hiring Challenges</h2>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm font-bold text-[#525252]"><CheckCircle size={16} className="text-[#B91C1C]" /> Private branded leaderboard</li>
                  <li className="flex items-center gap-2 text-sm font-bold text-[#525252]"><CheckCircle size={16} className="text-[#B91C1C]" /> Cross-college visibility (2000+ campuses)</li>
                  <li className="flex items-center gap-2 text-sm font-bold text-[#525252]"><CheckCircle size={16} className="text-[#B91C1C]" /> Direct, filtered candidate funnel</li>
                </ul>
                <Link to="/company/auth" className="inline-flex items-center gap-2 px-6 py-3 bg-[#B91C1C] text-white rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm">
                  Contact Sales <ArrowRight size={16} />
                </Link>
              </div>
              <div className="col-span-2 md:text-right border-t md:border-t-0 md:border-l border-[#B91C1C]/10 pt-6 md:pt-0 md:pl-8">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3] mb-1">Starting from</div>
                <div className="text-5xl font-black text-[#B91C1C] tracking-tighter mb-1">₹{pricing.national_challenge_price?.toLocaleString() || '9,999'}</div>
                <div className="text-sm font-bold text-[#525252]">per challenge</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works (Minimal) */}
      <section className="py-24 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-black text-[#0A0A0A] tracking-tight">3 Steps to Hire</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-0.5 bg-[#E5E5E5] z-0"></div>
            
            {[
              { num: '01', title: 'Post', desc: 'Create job or challenge.' },
              { num: '02', title: 'Test', desc: 'AI evaluates & ranks talent.' },
              { num: '03', title: 'Hire', desc: 'Interview top 1% and offer.' }
            ].map((step, i) => (
              <div key={i} className="relative z-10 text-center flex flex-col items-center">
                <div className="w-16 h-16 bg-white border-2 border-[#0A0A0A] rounded-full flex items-center justify-center text-xl font-black text-[#0A0A0A] mb-6">
                  {step.num}
                </div>
                <h3 className="text-xl font-black text-[#0A0A0A] mb-2">{step.title}</h3>
                <p className="text-sm font-medium text-[#525252]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white border-y border-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { q: "EvalentX reduced our hiring time by 60%. The AI assessments are incredibly accurate.", name: "Priya S.", role: "VP Eng @ TechHire" },
              { q: "We reached 10,000+ developers instantly. Best platform for entry-level tech hiring.", name: "Rahul M.", role: "CTO @ NexusLabs" },
              { q: "The proctored assessments give us 100% confidence in the candidate's actual skills.", name: "Anita D.", role: "HR @ Skyforge" }
            ].map((t, i) => (
              <div key={i} className="bg-[#FAFAF9] border border-[#E5E5E5] p-8 rounded-3xl">
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, idx) => <Star key={idx} size={14} className="fill-[#0A0A0A] text-[#0A0A0A]" />)}
                </div>
                <p className="text-[#171717] font-medium leading-relaxed mb-8">"{t.q}"</p>
                <div>
                  <div className="font-bold text-[#0A0A0A] text-sm">{t.name}</div>
                  <div className="text-xs font-bold text-[#A3A3A3] uppercase tracking-wide mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Band */}
      <section className="py-8 bg-[#0A0A0A] border-b border-[#171717]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap justify-center items-center gap-8 md:gap-16">
          <div className="text-[#525252] text-xs font-black uppercase tracking-widest">Enterprise Grade:</div>
          <div className="text-white/60 font-bold text-sm flex items-center gap-2"><Shield size={16}/> SOC 2 Type II</div>
          <div className="text-white/60 font-bold text-sm flex items-center gap-2"><Lock size={16}/> GDPR Ready</div>
          <div className="text-white/60 font-bold text-sm flex items-center gap-2"><CheckCircle size={16}/> ISO 27001 Aligned</div>
        </div>
      </section>

      {/* Hiring Outcomes — Grand case studies */}
      <section className="py-24 md:py-36 bg-white border-t border-[#E5E5E5]" data-testid="company-hiring-outcomes">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-4">Hiring Outcomes</p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Real teams. <br /><span className="text-[#B91C1C]">Real results.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed">
                EvalentX is how modern engineering teams cut hiring time, raise candidate quality, and remove bias from the funnel — backed by verified, proctored assessments.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                logo: 'TECHHIRE',
                stat: '60%',
                statLabel: 'Faster hires',
                title: 'Closed SDE-1 roles in 9 days, not 3 weeks.',
                desc: 'AI-ranked shortlists and proctored coding rounds replaced 4 manual screening stages.',
                testid: 'outcome-techhire',
              },
              {
                logo: 'NEXUSLABS',
                stat: '10K+',
                statLabel: 'Verified profiles',
                title: 'Hit national reach without recruiter ops.',
                desc: 'A 14-day branded contest funneled 10,000 pre-vetted developers into a single dashboard.',
                testid: 'outcome-nexuslabs',
              },
              {
                logo: 'SKYFORGE',
                stat: '100%',
                statLabel: 'Identity verified',
                title: 'Zero impersonation. Total signal.',
                desc: 'Every shortlist is identity-verified and AI-proctored — interview to offer with full confidence.',
                testid: 'outcome-skyforge',
              },
            ].map((o) => (
              <div
                key={o.logo}
                className="group bg-white border border-[#E5E5E5] rounded-2xl p-8 md:p-10 hover:border-[#B91C1C] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.18)] hover:-translate-y-1 transition-all duration-500"
                data-testid={o.testid}
              >
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-[#F5F5F5]">
                  <span className="text-sm font-extrabold tracking-[0.25em] text-[#171717]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                    {o.logo}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Case study</span>
                </div>
                <div className="mb-8">
                  <div className="text-5xl md:text-6xl font-extrabold tracking-tighter text-[#B91C1C] leading-none mb-2" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                    {o.stat}
                  </div>
                  <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em]">{o.statLabel}</p>
                </div>
                <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-[#0A0A0A] mb-4 leading-tight" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  {o.title}
                </h3>
                <p className="text-[#525252] text-[15px] leading-relaxed mb-8 font-medium">
                  {o.desc}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors">
                  Read story
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-32 md:py-40 bg-[#FAFAF9]" data-testid="company-cta-bottom">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-[#0A0A0A] mb-10 tracking-tighter" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
            Ready to hire from <br /><span className="text-[#B91C1C]">the top 1%?</span>
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/company/auth" className="px-10 py-5 bg-[#B91C1C] text-white rounded-full font-bold shadow-[0_8px_24px_rgba(185,28,28,0.25)] hover:shadow-[0_12px_32px_rgba(185,28,28,0.35)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 text-lg group">
              Get Started Free <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <GrandFooter />

      <style>{`
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      `}</style>
    </div>
  );
};

export default CompanyLandingPage;